import type { AxiosError } from 'axios';
import type { OptionType } from '../../util/types';
import React, { useMemo, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import { useMutation, useQuery } from 'react-query';
import { JobTypeEnum } from '@whizz/api-model';
import { repository } from '@whizz/api-client';
import Alert from '../alert/Alert';
import Selector from '../selector/Selector';
import { CancelButtonModal, SaveButton } from '../../global';
import { ModalFooter } from '../modal/Modal';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../util/vars';

// Matches the api's limit on one send.
const MAX_RECIPIENTS = 50;
const LEADER_JOB_TYPES: number[] = [ JobTypeEnum.PRINCIPAL, JobTypeEnum.DEPUTY, JobTypeEnum.MATHS_LEAD, JobTypeEnum.SENIOR_LEADERSHIP ];

interface Props {
  toggle: (open: boolean) => void;
  schoolId: number;
}

const EmailPlanModal = ({ toggle, schoolId }: Props): JSX.Element => {
  const { translate: t } = useI18n();

  const staffQuery = useQuery([ QUERIES_IDS.STAFF ], () => repository.StaffRepository.getStaff());

  // Only staff with an email can be sent the plan.
  const staff = useMemo(() => (staffQuery.data?.teachers ?? [])
    .filter((teacher) => !!teacher.email)
    .map((teacher) => ({ label: `${teacher.firstName} ${teacher.lastName ?? ''}`.trim(), value: teacher.id, jobType: teacher.jobType })),
  [ staffQuery.data ]);

  // School leaders are selected until the admin changes the selection.
  const [ chosenIds, setChosenIds ] = useState<number[] | null>(null);
  const selectedIds = chosenIds ?? staff.filter((member) => LEADER_JOB_TYPES.includes(member.jobType)).map((member) => member.value);

  const send = useMutation((teacherIds: number[]) => repository.SchoolImplementationPlanSelfRepository.emailPlan(schoolId, { teacherIds }));
  const errorStatus = (send.error as AxiosError | null)?.response?.status;
  const tooMany = MAX_RECIPIENTS < selectedIds.length;

  let sendLabel = t('pages.IMPLEMENTATION_PLAN.EMAIL.SEND');
  if (send.isLoading) {
    sendLabel = t('pages.IMPLEMENTATION_PLAN.EMAIL.SENDING');
  } else if (send.isSuccess) {
    sendLabel = t('pages.IMPLEMENTATION_PLAN.EMAIL.SENT', { count: send.data.recipientCount });
  }

  return (
    <>
      <div className={'container space-y-4 pt-md'}>
        <p>{t('pages.IMPLEMENTATION_PLAN.EMAIL.INTRO')}</p>
        <p className={'text-sm text-gray-500'}>{t('pages.IMPLEMENTATION_PLAN.EMAIL.AUTO_NOTE')}</p>
        <Selector
          id={'emailPlanRecipients'}
          label={t('pages.IMPLEMENTATION_PLAN.EMAIL.RECIPIENTS')}
          placeholder={t('pages.IMPLEMENTATION_PLAN.EMAIL.PLACEHOLDER')}
          options={staff}
          value={staff.filter((member) => selectedIds.includes(member.value))}
          onChange={(options: OptionType<number>[] | null) => setChosenIds((options ?? []).map((option) => option.value))}
          disabled={staffQuery.isLoading || send.isLoading || send.isSuccess}
          isLoading={staffQuery.isLoading}
          isMulti
        />
        {tooMany && <p role={'alert'} className={'text-negative text-sm'}>{t('pages.IMPLEMENTATION_PLAN.EMAIL.TOO_MANY', { max: MAX_RECIPIENTS })}</p>}
        <Alert show={send.isError} type={'error'}>
          {409 === errorStatus ? t('pages.IMPLEMENTATION_PLAN.EMAIL.EMPTY_PLAN') : t('pages.IMPLEMENTATION_PLAN.EMAIL.ERROR')}
        </Alert>
      </div>
      <ModalFooter justify={'between'}>
        <CancelButtonModal onClick={() => toggle(false)}>
          {send.isSuccess ? t('pages.IMPLEMENTATION_PLAN.EMAIL.CLOSE') : null}
        </CancelButtonModal>
        <SaveButton
          type={'button'}
          icon={GLOBAL_ICONS.message}
          isLoading={send.isLoading}
          disabled={!selectedIds.length || tooMany || send.isLoading || send.isSuccess}
          onClick={() => send.mutate(selectedIds)}
        >
          {sendLabel}
        </SaveButton>
      </ModalFooter>
    </>
  );
};

export { EmailPlanModal };
